import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { InputMask } from '@/components/InputMask/InputMask'
import { Button } from '@/components/Button/Button'
import './SettingsAreaGeneral.scoped.sass'
import { setMeField } from '@/core/store/Slices/MeSlice'
import { Auth } from 'aws-amplify'
import ToastService from '@/components/Toast/Toast.service'
import { updateMeFullname } from '@/core/http/me.api'
import TimezoneSelect from 'react-timezone-select'
import { BYODModal } from '@/pages/LoginPage/SignUp/steps/SignUpThirdStep/BYODModal/BYODModal'
import modalService from '@/services/modal.service'
import { PurchaseAnotherDomainModal } from './PurchaseAnotherDomainModal/PurchaseAnotherDomainModal'

export function SettingsAreaGeneral () {
  const dispatch = useDispatch()
  const meInfo = useSelector(state => state.me)
  const [submitting, setSubmitting] = useState(false)

  const [username, setUsername] = useState({
    value: meInfo.me.fullname,
    error: false,
    label: 'Full Name'
  })
  const [currentPass, setCurrentPass] = useState('')
  const [currentPassError, setCurrentPassError] = useState(false)
  const [newPass, setNewPass] = useState('')
  const [newPassError, setNewPassError] = useState(false)
  const [passwordChangeResult, setPasswordChangeResult] = useState(undefined)
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone)

  const handleNameChange = (value) => {
    let error = false
    // Validate image size here
    if (value === '') error = 'Username cannot be blank'
    setUsername({
      ...username,
      value,
      error,
    })
  }

  const onSubmitHandler = async () => {
    if (username.error) {
      return
    }
    setSubmitting(true)

    await updateMeFullname(username.value)
    dispatch(setMeField({
      field: 'fullname',
      value: username.value,
    }))
    ToastService.show({
      title: 'Full Name updated',
      message: 'Full name properly updated',
      type: 'success',
    })
    setSubmitting(false)
  }

  const handleCurrentPassChange = (value) => {
    setCurrentPass(value)
  }
  const handleNewPassChange = (value) => {
    setNewPass(value)
    if (value.length < 8 || !/(?=.*[a-z])(?=.*[A-Z]).{8,}/.test(value)) {
      setNewPassError('Password should have at least 8 characters combining uppercase and lowercase ones')
    } else {
      setNewPassError(undefined)
    }
  }

  const cleanState = () => {
    setCurrentPass('')
    setNewPass('')
  }

  const cleanUsernameChangeState = () => {
    setUsername({
      value: meInfo.me.fullname,
      error: false,
      label: 'Full Name'
    })
  }
  const validatePasswords = () => {
    if (!currentPass) setCurrentPassError('Password is empty')
    else setCurrentPassError('')
    if (!newPass) setNewPassError('Password is empty')
    else setNewPassError('')
    return currentPass && newPass
  }

  const onSubmitHandlerPass = async () => {
    if (!validatePasswords()) return
    const user = await Auth.currentAuthenticatedUser()

    try {
      const result = await Auth.changePassword(user, currentPass, newPass)
      if (result === 'SUCCESS') {
        ToastService.show({
          type: 'success',
          title: 'Password changed',
          message: 'Password successfully changed'
        })
        cleanState()
        setPasswordChangeResult(result)
        return true
      }
    } catch (e) {
      setPasswordChangeResult(e?.message)
      return false
    }
    // DO SOMETHING HERE
  }

  const onSubmitHandlerTimezone = (value) => {
    setTimezone(value)
    ToastService.show({
      type: 'success',
      title: 'Timezone changed',
      message: 'Password successfully changed'
    })
  }

  function promptBYODModal () {
    modalService.open(<BYODModal closeInsteadOfRedirect={true}/>)
  }
  
  return (
    <div className="settings-general">
      <div className="group">
        <div className="title">
          Account Settings
        </div>
        <div className="group-content">
          <div className="inputs">
            <div className="input-label">
              <div className="input-label-title">
                Full Name
              </div>
              <div className="input">
                <InputMask
                  errorMessage={username.error}
                  value={username.value}
                  onInput={handleNameChange}
                  placeholder={username.label} />
              </div>
            </div>
          </div>
        </div>
        <div className="bottom">
          <Button fullWidth={false} fullHeight={false} click={cleanUsernameChangeState} customClass="m-xxs" content="Cancel" type="secondary" />
          <Button disabled={submitting} click={onSubmitHandler} fullWidth={false} fullHeight={false} customClass="m-xxs" content="Save changes" />
        </div>
      </div>
      <div className='group'>
        <div className='group-content'>
          <div className='inputs'>
            <div className='input-label'>
              <div className='input-label-title timezone'>
                Timezone
              </div>
              <div className='input timezone'>
                <TimezoneSelect isSearchable={false} name='users-timezone' value={timezone} onChange={onSubmitHandlerTimezone}/>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="group">
        <div className="group-content change-password">
          <div className="inputs">
            <div className="input-label">
              <div className="input-label-title">
                Current password
              </div>
              <div className="input">
                <InputMask
                  type={'password'}
                  errorMessage={currentPassError}
                  value={currentPass}
                  onInput={handleCurrentPassChange}
                  placeholder={'Current password'} />
              </div>
            </div>
            <div className="input-label">
              <div className="input-label-title">
                Enter your new password
              </div>
              <div className="input">
                <InputMask
                  type={'password'}
                  errorMessage={newPassError}
                  value={newPass}
                  onInput={handleNewPassChange}
                  placeholder={'New password'}
                  />
              </div>
            </div>
            { passwordChangeResult !== 'SUCCESS' && <div className='pt-m'>{passwordChangeResult}</div>}
          </div>
          <div className="indications">
            <div className="indications-title">
              Password Requirements
            </div>
            <ol>
              <li>At least 8 characters</li>
              <li>At least one uppercase character</li>
              <li>At least one lowercase character</li>
            </ol>
          </div>
        </div>
        <div className="bottom">
          <Button click={cleanState} fullWidth={false} fullHeight={false} customClass="m-xxs" content="Cancel" type="secondary" />
          <Button disabled={submitting} fullWidth={false} fullHeight={false} customClass="m-xxs" content="Save changes" click={onSubmitHandlerPass} />
        </div>
      </div>
      <div className="group">
        <div className="group-content change-password">
          <div className="inputs">
            <div className="input-label">
              <div className="input-label-title">
                My Domains
              </div>
            </div>
            <div className="input-label">
              Main domain: {meInfo.me.defaultdomain}
            </div>
            {
            meInfo.me.domainList?.length > 0 && <div className="input-label">
              Other domains:
              {meInfo.me.domainsList?.map((domain) => <div key={domain}>{domain}</div>)}
              </div>
            }
          </div>
        </div>
        <div className="bottom">
          <Button disabled={meInfo.me.usermetadata?.requestedPurchasingAnotherDomain} click={() => { modalService.open(<PurchaseAnotherDomainModal/>) }} fullWidth={false} fullHeight={false} customClass="m-xxs" content="Buy another domain" type="secondary" />
          <Button disabled={submitting || meInfo.me.usermetadata?.byod } fullWidth={false} fullHeight={false} customClass="m-xxs" content="Bring my own domain" click={() => promptBYODModal()} />
        </div>
      </div>
      { /* <div className="group">
        <div className="title">
          Delete Account
        </div>
        <div className="group-content">
          <p>
            Deleting your account is permanent and cannot be reversed.
          </p>
        </div>
        <div className="bottom">
          <Button
            disabled={true}
            fullWidth={false}
            fullHeight={false}
            customClass="m-xxs"
            content="Delete Account"
            type="destructive" />
        </div>
      </div> */ }
    </div>
  )
}
